import React, { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { formatPersonalId, personalIdPattern } from "../../../region/region";
import { useFirmRegion } from "../../../region/useFirmRegion";

import { LABELS } from "../../../../app/constants/TextConstants";
import { ValidationConstants } from "../../../../app/constants/ValidationConstants";
import { SettingsInput } from "../../../../components/inputs/SettingsInput";
import { FieldLabel } from "../../../../components/newUI/text";
import SearchContacts from "../../../../components/searchContacts";
import CustomSelect from "../../../../components/select/Select";
import { SelectContacts } from "../../../../components/select/SelectContacts";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import {
  useAddContactMutation,
  useGetAddIndividualFormDataQuery,
  useGetContactTypesQuery,
  useGetPhoneTypesQuery,
  useLazyGetOrganizationSinQuery,
} from "../../../../slices/ContactsSlice";
import { useAddCustomerTagsMutation } from "../../../../slices/ContactTagsSlice";
import { useAddContactLinkedProfileMutation } from "../../../../slices/LinkedProfilesSlice";
import { GetPageTitle } from "../../../../support/ScrollToTop";
import { SelectOptionType } from "../../../../types/types";
import { validateEmail } from "../../../../utils/email";
import { useDuplicateEmailWarning } from "../../duplicateEmail/useDuplicateEmailWarning";
import {
  duplicatePersonalIdText,
  NUMBER_CHECK_FAILED,
  numberCheckResult,
} from "../../utils/duplicateNumber";
import {
  DISPLAY_NAME_MAX,
  DISPLAY_NAME_RULE,
  NAME_PART_RULE,
  PERSON_NAME_DISALLOWED,
  PERSON_NAME_PATTERN,
} from "../../utils/nameRules";
import { phoneNumberProblem } from "../../utils/phoneNumber";
import { relationshipProblem } from "../../utils/relationship";
import {
  ContactKind,
  ContactTypeSelector,
  formatPhoneNumber,
  parseMutationError,
} from "./addContactForm.helpers";
import { AddContactFormFooter } from "./components/AddContactFormFooter";
import { AddContactFormHeader } from "./components/AddContactFormHeader";
import { AddContactSection } from "./components/AddContactSection";
import { ContactTagsSelector } from "./ContactTagsSelector";
import { ADD_CONTACT_INPUT_CLASSES } from "./model/addContact.constants";

interface IAddIndividualProps {
  setIsLoading?: (isLoading: boolean) => void;
  closeModal: () => void;
  closeAddCustomerPopupModal: () => void;
  setContactsForSort?: (newContact: {
    id: string;
    name: string;
    isBusinessContact: boolean;
    customerId: string;
  }) => void;
  isSearch?: boolean;
  isIdLikeContactId?: boolean;
  /** Pre-filled form (Ask ProCharted actions); nothing is created until Create. */
  initialValues?: Partial<
    Pick<
      FormData,
      | "firstName"
      | "middleName"
      | "lastName"
      | "displayName"
      | "email"
      | "phone"
    >
  > & { contactType?: ContactKind };
  /** Called after a successful Create, before the window navigates. */
  onCreated?: (created: {
    id: string;
    customerId: string;
    name: string;
  }) => void;
}

type FormData = {
  phoneNumberTypeEnumId: SelectOptionType | null;
  firstName: string;
  middleName: string;
  lastName: string;
  displayName: string;
  email: string;
  phone: string;
  socialIdentificationNumber: string;
  linkedProfile: SelectOptionType | null;
  linkedProfileRelation: string;
  tags: SelectOptionType[];
};

type NewIndividualContact = {
  id?: string;
  customerId?: string;
  firstName?: string;
  lastName?: string;
};

type NameFieldName = "firstName" | "middleName" | "lastName";

const defaultFormValues: FormData = {
  phoneNumberTypeEnumId: null,
  firstName: "",
  middleName: "",
  lastName: "",
  displayName: "",
  email: "",
  phone: "",
  socialIdentificationNumber: "",
  linkedProfile: null,
  linkedProfileRelation: "",
  tags: [],
};

export const AddIndividualView: React.FC<IAddIndividualProps> = ({
  closeAddCustomerPopupModal,
  isSearch,
  setContactsForSort,
  isIdLikeContactId,
  closeModal,
  initialValues,
  onCreated,
}) => {
  const navigate = useNavigate();
  const linkedFirm = getLinkedFirm();
  const [contactType, setContactType] = useState<ContactKind>(
    initialValues?.contactType || "Client",
  );
  const [duplicateSinError, setDuplicateSinError] = useState("");
  const region = useFirmRegion();
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const { data: phoneTypes = [] } = useGetPhoneTypesQuery();
  const { data: contactTypes = [] } = useGetContactTypesQuery();
  const { data: addIndividualData } = useGetAddIndividualFormDataQuery();

  const [addContact, { error: addCustomerError }] = useAddContactMutation();
  const [addCustomerTags] = useAddCustomerTagsMutation();
  const [addContactLinkedProfile] = useAddContactLinkedProfileMutation();
  const [getExistSin] = useLazyGetOrganizationSinQuery();
  const { confirmEmails, dialog: duplicateEmailDialog } =
    useDuplicateEmailWarning();

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    control,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      ...defaultFormValues,
      firstName: initialValues?.firstName || "",
      middleName: initialValues?.middleName || "",
      lastName: initialValues?.lastName || "",
      displayName: initialValues?.displayName || "",
      email: initialValues?.email || "",
      phone: initialValues?.phone || "",
    },
  });

  const { profileRules, contactNameRule } = ValidationConstants.customerRules;

  useEffect(() => {
    document.title = GetPageTitle("Add Customer");
  }, []);

  useEffect(() => {
    setValue("phoneNumberTypeEnumId", phoneTypes[0] || null);
  }, [phoneTypes, setValue]);

  const closeForm = useCallback(() => {
    closeModal();
    closeAddCustomerPopupModal();
  }, [closeAddCustomerPopupModal, closeModal]);

  const updateDisplayName = useCallback(
    (nextValue?: Partial<Pick<FormData, NameFieldName>>) => {
      const values = getValues();
      const firstName = nextValue?.firstName ?? values.firstName;
      const middleName = nextValue?.middleName ?? values.middleName;
      const lastName = nextValue?.lastName ?? values.lastName;
      const displayName = [firstName, middleName, lastName]
        .map((value) => value.trim())
        .filter(Boolean)
        .join(" ");

      setValue("displayName", displayName, { shouldValidate: true });
    },
    [getValues, setValue],
  );

  const selectedContactTypeId = contactTypes.find(
    (type) => type.label === contactType,
  )?.value;

  const onSubmit = async (data: FormData) => {
    setDuplicateSinError("");
    setIsSubmittingContact(true);

    const orgId = linkedFirm?.orgId?.toUpperCase();
    const sin = data.socialIdentificationNumber.trim().toUpperCase();

    if (sin) {
      try {
        await getExistSin({ orgId, sin, businessNumber: "" }).unwrap();
      } catch (error) {
        setDuplicateSinError(
          numberCheckResult(error) === "duplicate"
            ? duplicatePersonalIdText(region.personalId.label)
            : NUMBER_CHECK_FAILED,
        );
        setIsSubmittingContact(false);
        return;
      }
    }

    // Roadmap 1784: warn (never block) when another client uses this email.
    if (!(await confirmEmails([data.email]))) {
      setIsSubmittingContact(false);
      return;
    }

    try {
      const newContact = (await addContact({
        customerTypeEnumId: addIndividualData?.customerType.id,
        orgId: linkedFirm?.orgId,
        locationId: linkedFirm?.locationId || null,
        email: data.email,
        isPrimaryEmail: true,
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        middleName: data.middleName.trim(),
        displayName: data.displayName,
        socialIdentificationNumber: data.socialIdentificationNumber,
        phoneNumberTypeEnumId: data.phoneNumberTypeEnumId?.value,
        contactTypeEnumId: selectedContactTypeId,
        phoneNumber: data.phone,
      }).unwrap()) as NewIndividualContact;

      try {
        if (newContact.customerId && data.tags.length > 0) {
          await addCustomerTags({
            customerId: newContact.customerId,
            tagIds: data.tags.map((tag) => tag.value),
          }).unwrap();
        }

        if (newContact.id && data.linkedProfile?.value) {
          await addContactLinkedProfile({
            contactId: newContact.id,
            linkedCustomerId: data.linkedProfile.value,
            relation: data.linkedProfileRelation,
          }).unwrap();
        }
      } catch {
        // Contact is already created. Keep the previous non-blocking behavior for optional links.
      }

      onCreated?.({
        id: `${newContact.id || ""}`,
        customerId: `${newContact.customerId || ""}`,
        name: data.displayName || `${data.firstName} ${data.lastName}`.trim(),
      });

      if (isSearch) {
        if (isIdLikeContactId) {
          closeModal();
        }

        closeAddCustomerPopupModal();
        setContactsForSort?.({
          name: `${newContact.firstName || ""} ${newContact.lastName || ""}`,
          id: isIdLikeContactId
            ? `${newContact.id || ""}`
            : `${newContact.customerId || ""}`,
          customerId: `${newContact.customerId || ""}`,
          isBusinessContact: false,
        });
        return;
      }

      if (newContact.customerId) {
        navigate(`/contact/${newContact.customerId}?`);
      }
      closeForm();
    } catch {
      setIsSubmittingContact(false);
    }
  };

  const renderNameField = (
    name: NameFieldName,
    label: string,
    placeholder: string,
    required = false,
  ) => (
    <div
      className={
        "form-control flex flex-col items-start justify-start max-md:w-full"
      }>
      <FieldLabel label={label} required={required} />
      {/* PHONE-016 (QA batch 17): full width, one under the other on phones
          (three 150px boxes didn't fit a 390px screen). */}
      <div className={"w-[150px] max-md:w-full"}>
        <Controller
          control={control}
          rules={{
            ...contactNameRule.valueLength,
            required: required ? LABELS.required : false,
            pattern: {
              value: PERSON_NAME_PATTERN,
              message: NAME_PART_RULE,
            },
            validate: required
              ? (value) => value.trim().length > 0 || LABELS.required
              : undefined,
          }}
          render={({ field: { value } }) => (
            <SearchContacts
              setValue={(newValue) => {
                const trimmedValue = newValue.trim();
                setValue(name, trimmedValue, { shouldValidate: true });
                updateDisplayName({ [name]: trimmedValue });
              }}
              removeSelectIcon
              value={value}
              placeholder={placeholder}
              close={closeForm}
              type={"contact"}
            />
          )}
          name={name}
        />
      </div>
      {errors[name]?.message && (
        <span className={"pl-1 pt-2 text-xs font-bold text-error"}>
          {errors[name]?.message}
        </span>
      )}
    </div>
  );

  return (
    <>
      <AddContactFormHeader
        title={"New Individual"}
        onClose={closeAddCustomerPopupModal}
      />
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={"flex max-h-[calc(100vh-96px)] flex-col"}>
        <div className={"overflow-y-auto px-6 pb-6 pt-4"}>
          {addCustomerError && (
            <div
              className={
                "my-2 rounded-md border border-gray-300 p-2 text-sm font-semibold text-error"
              }>
              {parseMutationError(addCustomerError)}
            </div>
          )}

          <div
            className={
              "flex items-start justify-between gap-2 max-md:flex-col max-md:gap-3"
            }>
            {renderNameField("firstName", "First Name", "First name", true)}
            {renderNameField("middleName", "Middle Name", "Middle name")}
            {renderNameField("lastName", "Last Name", "Last name", true)}
          </div>

          <div className={"form-control mt-5 lg:col-span-5"}>
            <FieldLabel label={"Display Name"} />
            <SettingsInput
              type={"text"}
              variant={"form"}
              focusStyle={"select"}
              size={"md"}
              inputClasses={`w-full ${ADD_CONTACT_INPUT_CLASSES}`}
              placeholder={"Display Name"}
              {...register("displayName", {
                pattern: {
                  value: PERSON_NAME_PATTERN,
                  message: DISPLAY_NAME_RULE,
                },
                maxLength: {
                  value: DISPLAY_NAME_MAX,
                  message: `Must not be more than ${DISPLAY_NAME_MAX} characters`,
                },
              })}
              onInput={(event: React.FormEvent<HTMLInputElement>) => {
                const input = event.currentTarget;
                input.value = input.value.replace(PERSON_NAME_DISALLOWED, "");
              }}
            />
            {errors.displayName?.message && (
              <span className={"pt-2 text-xs font-bold text-error"}>
                {errors.displayName.message}
              </span>
            )}
          </div>

          <ContactTypeSelector value={contactType} onChange={setContactType} />

          <div className={"form-control mt-5 lg:col-span-5"}>
            <FieldLabel label={region.personalId.label} />
            <SettingsInput
              placeholder={region.personalId.placeholder}
              {...register("socialIdentificationNumber", {
                pattern: {
                  value: personalIdPattern(region.code),
                  message: region.personalId.invalid,
                },
                onChange: (event) => {
                  setDuplicateSinError("");
                  setValue(
                    "socialIdentificationNumber",
                    formatPersonalId(region.code, event.target.value),
                    { shouldValidate: true },
                  );
                },
              })}
              type={"text"}
              inputMode={region.code === "GB" ? "text" : "numeric"}
              maxLength={11}
              variant={"form"}
              focusStyle={"select"}
              size={"md"}
              inputClasses={`w-full ${ADD_CONTACT_INPUT_CLASSES}`}
            />
            {duplicateSinError && (
              <div className={"text-sm font-semibold text-error"}>
                {duplicateSinError}
              </div>
            )}
            {errors.socialIdentificationNumber?.message && (
              <span className={"pt-2 text-xs font-bold text-error"}>
                {errors.socialIdentificationNumber.message}
              </span>
            )}
          </div>

          <div className={"form-control mt-5"}>
            <FieldLabel label={"Email Address"} />
            <SettingsInput
              type={"text"}
              {...register("email", {
                ...profileRules.emailLength,
                validate: validateEmail("Invalid email address format"),
              })}
              variant={"form"}
              focusStyle={"select"}
              size={"md"}
              inputClasses={`w-full ${ADD_CONTACT_INPUT_CLASSES}`}
              placeholder={"Email address"}
            />
            {errors.email && (
              <span className={"pt-2 text-xs font-bold text-error"}>
                {errors.email.message}
              </span>
            )}
          </div>

          <div className={"mt-5 flex gap-4 max-md:gap-2"}>
            <div className={"flex shrink-0 flex-col"}>
              <FieldLabel label={"Phone Number"} />
              <div className={"w-[150px] max-md:w-[120px]"}>
                <Controller
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <CustomSelect
                      options={phoneTypes}
                      value={value}
                      onChange={(newValue) => {
                        onChange(newValue as SelectOptionType);
                      }}
                      withMenu
                      selectStyle={{
                        size: "md",
                        fullWidth: true,
                        fontWeight: 400,
                      }}
                    />
                  )}
                  name={"phoneNumberTypeEnumId"}
                />
              </div>

              {errors.phoneNumberTypeEnumId?.message && (
                <span className={"pt-2 text-xs font-bold text-error"}>
                  {errors.phoneNumberTypeEnumId.message}
                </span>
              )}
            </div>
            <div className={"flex w-full min-w-0 flex-col justify-end"}>
              <SettingsInput
                type={"text"}
                inputMode={"tel"}
                {...register("phone", {
                  validate: (value) => phoneNumberProblem(value) || true,
                  onChange: (event) => {
                    setValue("phone", formatPhoneNumber(event.target.value));
                  },
                })}
                variant={"form"}
                focusStyle={"select"}
                size={"md"}
                inputClasses={`w-full ${ADD_CONTACT_INPUT_CLASSES}`}
                placeholder={"Phone number"}
              />
              {errors.phone && (
                <span className={"pt-2 text-sm font-bold text-error"}>
                  {errors.phone.message}
                </span>
              )}
            </div>
          </div>

          <AddContactSection
            title={"Linked Profile"}
            description={"Link another profile for quick access."}
            className={"mt-6 border-t border-gray-200 pt-5"}>
            <div
              className={"grid grid-cols-[1.5fr_1fr] gap-3 max-md:grid-cols-1"}>
              <div>
                <FieldLabel label={"Contact"} className={"!mb-1 text-xs"} />
                <Controller
                  control={control}
                  name={"linkedProfile"}
                  render={({ field: { value, onChange } }) => (
                    <SelectContacts
                      value={value}
                      onChange={(data) => onChange(data)}
                    />
                  )}
                />
              </div>
              <div>
                <FieldLabel
                  label={"Relationship"}
                  className={"!mb-1 text-xs"}
                />
                <SettingsInput
                  type={"text"}
                  {...register("linkedProfileRelation", {
                    validate: (value) => relationshipProblem(value) || true,
                  })}
                  variant={"form"}
                  focusStyle={"select"}
                  size={"md"}
                  inputClasses={`w-full ${ADD_CONTACT_INPUT_CLASSES}`}
                  placeholder={"Role"}
                />
                {errors.linkedProfileRelation?.message && (
                  <span className={"pt-2 text-xs font-bold text-error"}>
                    {errors.linkedProfileRelation.message}
                  </span>
                )}
              </div>
            </div>
          </AddContactSection>

          <div className={"mt-5"}>
            <FieldLabel label={"Tags"} />
            <ContactTagsSelector
              value={watch("tags")}
              onChange={(tags) => setValue("tags", tags, { shouldDirty: true })}
            />
          </div>
        </div>

        <AddContactFormFooter
          onCancel={closeAddCustomerPopupModal}
          isSubmitting={isSubmittingContact}
        />
      </form>
      {duplicateEmailDialog}
    </>
  );
};
